<template>
  <div id="settings-user">
    <v-card class="su-card" elevation="0">

      <!-- Header -->
      <div class="text-center pt-8 pb-4 px-6">
        <div class="su-header-icon mx-auto mb-4">
          <v-icon color="#22d3ee" size="28">mdi-account-cog-outline</v-icon>
        </div>
        <div class="su-card-title">{{ $t('settings.title') }}</div>
        <div class="su-card-subtitle mt-1">{{ $t('settings.subtitle') }}</div>
      </div>

      <v-divider class="su-divider" />

      <!-- Tabs -->
      <v-tabs v-model="tab" color="#22d3ee" class="su-tabs">
        <v-tab>
          <v-icon left small>mdi-bell-outline</v-icon>
          {{ $t('settings.tabs.notifications') }}
        </v-tab>
        <v-tab>
          <v-icon left small>mdi-shield-outline</v-icon>
          {{ $t('settings.tabs.privacy') }}
        </v-tab>
        <v-tab>
          <v-icon left small>mdi-palette-outline</v-icon>
          {{ $t('settings.tabs.appearance') }}
        </v-tab>
      </v-tabs>

      <v-divider class="su-divider" />

      <v-tabs-items v-model="tab">

        <!-- ── Notificações ─────────────────────────── -->
        <v-tab-item>
          <div class="su-tab-body">
            <div class="su-section-title mb-5">{{ $t('settings.notifications.title') }}</div>

            <div class="su-section-label mb-2">{{ $t('settings.notifications.notifyWhen') }}</div>

            <div class="su-toggle-row">
              <span class="su-toggle-label">{{ $t('settings.notifications.newFeedback') }}</span>
              <v-switch v-model="notificationSettings.newFeedback" color="#22d3ee" dense hide-details class="su-toggle ma-0" />
            </div>
            <div class="su-toggle-row">
              <span class="su-toggle-label">{{ $t('settings.notifications.oneOnOneScheduled') }}</span>
              <v-switch v-model="notificationSettings.oneOnOneScheduled" color="#22d3ee" dense hide-details class="su-toggle ma-0" />
            </div>
            <div class="su-toggle-row">
              <span class="su-toggle-label">{{ $t('settings.notifications.pdiUpdated') }}</span>
              <v-switch v-model="notificationSettings.pdiUpdated" color="#22d3ee" dense hide-details class="su-toggle ma-0" />
            </div>
            <div class="su-toggle-row su-toggle-row--last">
              <span class="su-toggle-label">{{ $t('settings.notifications.goalDeadline') }}</span>
              <v-switch v-model="notificationSettings.goalDeadline" color="#22d3ee" dense hide-details class="su-toggle ma-0" />
            </div>

            <div class="su-section-label mt-5 mb-2">{{ $t('settings.notifications.channelsSection') }}</div>

            <v-checkbox v-model="notificationSettings.channels" :label="$t('settings.notifications.channelEmail')" value="email" color="#22d3ee" dense hide-details class="su-check mb-1" />
            <v-checkbox v-model="notificationSettings.channels" :label="$t('settings.notifications.channelPush')"  value="push"  color="#22d3ee" dense hide-details class="su-check mb-1" />
            <v-checkbox v-model="notificationSettings.channels" :label="$t('settings.notifications.channelSms')"   value="sms"   color="#22d3ee" dense hide-details class="su-check mb-4" />

            <div class="su-field-label mb-1">{{ $t('settings.notifications.digestFrequency') }}</div>
            <v-select
              v-model="notificationSettings.digestFrequency"
              :items="digestFrequencyOptions"
              outlined
              dense
              hide-details
              class="su-select mb-4"
            />

            <v-checkbox v-model="localGroupEmail" :label="$t('settings.notifications.groupEmail')" color="#22d3ee" dense hide-details class="su-check" />
          </div>
        </v-tab-item>

        <!-- ── Privacidade ──────────────────────────── -->
        <v-tab-item>
          <div class="su-tab-body">
            <div class="su-section-title mb-5">{{ $t('settings.privacy.title') }}</div>

            <div class="su-section-label mb-2">{{ $t('settings.privacy.profileVisibility') }}</div>

            <div class="su-toggle-row">
              <span class="su-toggle-label">{{ $t('settings.privacy.showEmail') }}</span>
              <v-switch v-model="privacySettings.showEmail" color="#22d3ee" dense hide-details class="su-toggle ma-0" />
            </div>
            <div class="su-toggle-row">
              <span class="su-toggle-label">{{ $t('settings.privacy.showPhone') }}</span>
              <v-switch v-model="privacySettings.showPhone" color="#22d3ee" dense hide-details class="su-toggle ma-0" />
            </div>
            <div class="su-toggle-row su-toggle-row--last">
              <span class="su-toggle-label">{{ $t('settings.privacy.showSkills') }}</span>
              <v-switch v-model="privacySettings.showSkills" color="#22d3ee" dense hide-details class="su-toggle ma-0" />
            </div>

            <div class="su-section-label mt-5 mb-2">{{ $t('settings.privacy.feedbackSection') }}</div>

            <div class="su-toggle-row su-toggle-row--last">
              <span class="su-toggle-label">{{ $t('settings.privacy.allowAnonymousFeedback') }}</span>
              <v-switch v-model="privacySettings.allowAnonymousFeedback" color="#22d3ee" dense hide-details class="su-toggle ma-0" />
            </div>

            <div class="su-field-label mt-5 mb-1">{{ $t('settings.privacy.feedbackVisibility') }}</div>
            <v-select
              v-model="privacySettings.feedbackVisibility"
              :items="visibilityOptions"
              outlined
              dense
              hide-details
              class="su-select mb-5"
            />

            <div class="su-section-label mb-2">{{ $t('settings.privacy.dataSharing') }}</div>

            <div class="su-toggle-row">
              <span class="su-toggle-label">{{ $t('settings.privacy.shareWithManager') }}</span>
              <v-switch v-model="privacySettings.shareProgressWithManager" color="#22d3ee" dense hide-details class="su-toggle ma-0" />
            </div>
            <div class="su-toggle-row su-toggle-row--last">
              <span class="su-toggle-label">{{ $t('settings.privacy.shareWithTeam') }}</span>
              <v-switch v-model="privacySettings.shareProgressWithTeam" color="#22d3ee" dense hide-details class="su-toggle ma-0" />
            </div>
          </div>
        </v-tab-item>

        <!-- ── Aparência ────────────────────────────── -->
        <v-tab-item>
          <div class="su-tab-body">
            <div class="su-section-title mb-5">{{ $t('settings.appearance.title') }}</div>

            <div class="su-section-label mb-3">{{ $t('settings.appearance.themeSection') }}</div>
            <div class="su-theme-grid mb-5">
              <div
                v-for="theme in themeOptions"
                :key="theme.value"
                class="su-theme-option"
                :class="{ active: appearanceSettings.theme === theme.value }"
                @click="selectTheme(theme.value)"
              >
                <v-icon size="28" :color="appearanceSettings.theme === theme.value ? '#22d3ee' : '#888888'">{{ theme.icon }}</v-icon>
                <div class="su-theme-label">{{ theme.text }}</div>
                <div class="su-theme-desc">{{ theme.description }}</div>
              </div>
            </div>

            <div class="su-field-label mb-2">{{ $t('settings.appearance.primaryColor') }}</div>
            <div class="su-color-swatches mb-4">
              <div
                v-for="c in colorOptions"
                :key="c.value"
                class="su-color-dot"
                :class="{ active: appearanceSettings.primaryColor === c.value }"
                :style="{ backgroundColor: colorHexMap[c.value] }"
                :title="c.text"
                @click="appearanceSettings.primaryColor = c.value"
              >
                <v-icon v-if="appearanceSettings.primaryColor === c.value" size="16" color="white">mdi-check</v-icon>
              </div>
            </div>

            <div class="su-field-label mb-1">{{ $t('settings.appearance.fontSize') }}</div>
            <v-select
              v-model="appearanceSettings.fontSize"
              :items="fontSizeOptions"
              outlined
              dense
              hide-details
              class="su-select mb-5"
            />

            <div class="su-section-label mb-2">{{ $t('settings.appearance.interfaceSection') }}</div>

            <div class="su-toggle-row">
              <div>
                <span class="su-toggle-label">{{ $t('settings.appearance.compactMode') }}</span>
                <div class="su-toggle-hint">{{ $t('settings.appearance.compactModeHint') }}</div>
              </div>
              <v-switch v-model="appearanceSettings.compactMode" color="#22d3ee" dense hide-details class="su-toggle ma-0" />
            </div>
            <div class="su-toggle-row su-toggle-row--last">
              <div>
                <span class="su-toggle-label">{{ $t('settings.appearance.animations') }}</span>
                <div class="su-toggle-hint">{{ $t('settings.appearance.animationsHint') }}</div>
              </div>
              <v-switch v-model="appearanceSettings.animations" color="#22d3ee" dense hide-details class="su-toggle ma-0" />
            </div>

            <!-- ── Idioma ── -->
            <div class="su-section-label mt-5 mb-3">{{ $t('settings.appearance.languageSection') }}</div>
            <div class="su-field-label mb-1">{{ $t('settings.appearance.languageLabel') }}</div>
            <v-select
              v-model="appearanceSettings.language"
              :items="languageOptions"
              outlined
              dense
              hide-details
              class="su-select mb-1"
              prepend-inner-icon="mdi-translate"
            />
            <div class="su-toggle-hint mt-1 mb-2">{{ $t('settings.appearance.languageHint') }}</div>
          </div>
        </v-tab-item>

      </v-tabs-items>

      <!-- Botões -->
      <v-divider class="su-divider" />
      <div class="su-footer d-flex justify-end px-6 py-4" style="gap: 10px">
        <v-btn
          depressed
          class="su-btn-save text-none"
          :loading="loading"
          :disabled="loading"
          @click="saveSettings"
        >
          <v-icon left small>mdi-content-save-outline</v-icon>
          {{ $t('settings.actions.save') }}
        </v-btn>
        <v-btn
          outlined
          class="su-btn-reset text-none"
          :disabled="loading"
          @click="resetSettings"
        >
          <v-icon left small>mdi-restore</v-icon>
          {{ $t('settings.actions.reset') }}
        </v-btn>
      </div>
    </v-card>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="3000" top>
      {{ snackbarText }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false">{{ $t('settings.actions.close') }}</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { UserConfigService } from '@/services'
import { loadLanguageAsync, BACKEND_TO_LOCALE } from '@/i18n'

export default {
  name: 'SettingsUser',

  data: () => ({
    tab: 0,
    loading: false,
    localGroupEmail: false,
    notificationSettings: {
      newFeedback: true,
      oneOnOneScheduled: true,
      pdiUpdated: true,
      campaignInvite: true,
      goalDeadline: true,
      skillEndorsement: true,
      teamAnnouncement: true,
      platformUpdates: false,
      channels: ['email', 'push'],
      digestFrequency: 'DAILY',
    },
    privacySettings: {
      showEmail: false,
      showPhone: false,
      showSocialMedia: true,
      showSkills: true,
      allowAnonymousFeedback: true,
      showFeedbackStats: true,
      feedbackVisibility: 'MANAGER_ONLY',
      shareProgressWithManager: true,
      shareProgressWithTeam: false,
    },
    appearanceSettings: {
      theme: 'LIGHT',
      primaryColor: 'teal',
      fontSize: 'MEDIUM',
      dashboardLayout: 'CARDS',
      compactMode: false,
      showAvatars: true,
      animations: true,
      language: 'PT_BR',
    },
    colorOptions: [
      { text: 'Ciano',   value: 'cyan'   },
      { text: 'Azul',    value: 'blue'   },
      { text: 'Índigo',  value: 'indigo' },
      { text: 'Roxo',    value: 'purple' },
      { text: 'Verde',   value: 'teal'   },
      { text: 'Laranja', value: 'orange' },
      { text: 'Rosa',    value: 'pink'   },
    ],
    colorHexMap: {
      cyan:   '#22d3ee',
      blue:   '#3b82f6',
      indigo: '#6366f1',
      purple: '#a855f7',
      teal:   '#14b8a6',
      orange: '#f97316',
      pink:   '#ec4899',
    },
    snackbar: false,
    snackbarText: '',
    snackbarColor: 'success',
  }),

  computed: {
    ...mapGetters(['userConfig']),

    languageOptions () {
      return [
        { text: this.$t('languages.PT_BR'), value: 'PT_BR' },
        { text: this.$t('languages.EN'),    value: 'EN'    },
        { text: this.$t('languages.ES'),    value: 'ES'    },
      ]
    },

    digestFrequencyOptions () {
      return [
        { text: this.$t('settings.notifications.digestNever'),  value: 'NEVER'  },
        { text: this.$t('settings.notifications.digestDaily'),  value: 'DAILY'  },
        { text: this.$t('settings.notifications.digestWeekly'), value: 'WEEKLY' },
      ]
    },

    visibilityOptions () {
      return [
        { text: this.$t('settings.privacy.visPrivate'),     value: 'PRIVATE'      },
        { text: this.$t('settings.privacy.visManagerOnly'), value: 'MANAGER_ONLY' },
        { text: this.$t('settings.privacy.visTeam'),        value: 'TEAM'         },
        { text: this.$t('settings.privacy.visPublic'),      value: 'PUBLIC'       },
      ]
    },

    themeOptions () {
      return [
        { text: this.$t('settings.appearance.themeLight'), value: 'LIGHT', icon: 'mdi-white-balance-sunny',  description: this.$t('settings.appearance.themeLightDesc') },
        { text: this.$t('settings.appearance.themeDark'),  value: 'DARK',  icon: 'mdi-moon-waning-crescent', description: this.$t('settings.appearance.themeDarkDesc')  },
        { text: this.$t('settings.appearance.themeAuto'),  value: 'AUTO',  icon: 'mdi-theme-light-dark',     description: this.$t('settings.appearance.themeAutoDesc')  },
      ]
    },

    fontSizeOptions () {
      return [
        { text: this.$t('settings.appearance.fontSmall'),  value: 'SMALL'  },
        { text: this.$t('settings.appearance.fontMedium'), value: 'MEDIUM' },
        { text: this.$t('settings.appearance.fontLarge'),  value: 'LARGE'  },
      ]
    },
  },

  watch: {
    'appearanceSettings.language' (newLang) {
      if (!newLang) return
      const locale = BACKEND_TO_LOCALE[newLang] || 'pt-BR'
      loadLanguageAsync(locale)
    },
  },

  async mounted () {
    await this.loadSettings()
  },

  methods: {
    async loadSettings () {
      try {
        this.loading = true
        if (!this.userConfig) {
          await this.$store.dispatch('loadUserConfig')
        }
        if (this.userConfig) {
          this.notificationSettings = { ...this.userConfig.notificationSettings }
          this.privacySettings      = { ...this.userConfig.privacySettings }
          this.appearanceSettings   = {
            language: 'PT_BR',
            ...this.userConfig.appearanceSettings,
          }
          // Apply saved language immediately
          const savedLang = this.appearanceSettings.language
          if (savedLang) {
            await loadLanguageAsync(BACKEND_TO_LOCALE[savedLang] || 'pt-BR')
          }
        }
      } catch (error) {
        console.error('Erro ao carregar configurações:', error)
      } finally {
        this.loading = false
      }
    },

    selectTheme (theme) {
      this.appearanceSettings.theme = theme
      this.applyTheme(theme)
    },

    applyTheme (theme) {
      let isDark = false
      if (theme === 'AUTO') {
        isDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
      } else {
        isDark = theme === 'DARK'
      }
      this.$vuetify.theme.dark = isDark
      localStorage.setItem('elevment_dark_mode', isDark)
      document.documentElement.setAttribute('data-theme', isDark ? 'dark' : 'light')
      this.$root.$emit('theme-changed', isDark)
    },

    async saveSettings () {
      try {
        this.loading = true
        const config = {
          notificationSettings: this.notificationSettings,
          privacySettings:      this.privacySettings,
          appearanceSettings:   this.appearanceSettings,
        }
        await this.$store.dispatch('updateUserConfig', config)
        this.snackbarText  = this.$t('settings.messages.saved')
        this.snackbarColor = 'success'
        this.snackbar      = true
      } catch (error) {
        console.error('Erro ao salvar configurações:', error)
        this.snackbarText  = this.$t('settings.messages.saveError')
        this.snackbarColor = 'error'
        this.snackbar      = true
      } finally {
        this.loading = false
      }
    },

    async resetSettings () {
      try {
        this.loading = true
        await UserConfigService.resetConfig()
        await this.$store.dispatch('loadUserConfig')
        await this.loadSettings()
        this.snackbarText  = this.$t('settings.messages.reset')
        this.snackbarColor = 'info'
        this.snackbar      = true
      } catch (error) {
        console.error('Erro ao restaurar configurações:', error)
        this.snackbarText  = this.$t('settings.messages.resetError')
        this.snackbarColor = 'error'
        this.snackbar      = true
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
/* ── Wrapper ──────────────────────────────────────────── */
#settings-user {
  display: flex;
  justify-content: center;
  padding: 32px 16px 48px;
}

/* ── Card ─────────────────────────────────────────────── */
.su-card {
  width: 100%;
  max-width: 680px;
  border-radius: 14px !important;
  border: 1px solid rgba(0,0,0,0.08) !important;
  background: #ffffff !important;
}

/* ── Header icon ──────────────────────────────────────── */
.su-header-icon {
  width: 52px;
  height: 52px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(34,211,238,0.12);
}

.su-card-title {
  font-size: 18px;
  font-weight: 700;
  letter-spacing: -0.01em;
}

.su-card-subtitle {
  font-size: 13px;
  color: #888;
}

/* ── Dividers ─────────────────────────────────────────── */
.su-divider {
  border-color: rgba(0,0,0,0.06) !important;
}

/* ── Tabs ─────────────────────────────────────────────── */
.su-tabs {
  border-bottom: none !important;
}

.su-tabs ::v-deep .v-tab--active {
  color: #22d3ee !important;
}

.su-tabs ::v-deep .v-tabs-slider-wrapper .v-tabs-slider {
  background-color: #22d3ee !important;
  color: #22d3ee !important;
}

/* ── Tab body ─────────────────────────────────────────── */
.su-tab-body {
  padding: 24px;
  min-height: 320px;
}

.su-section-title {
  font-size: 15px;
  font-weight: 600;
}

.su-section-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #888;
}

.su-field-label {
  font-size: 13px;
  font-weight: 500;
  color: #666;
}

/* ── Toggle rows ──────────────────────────────────────── */
.su-toggle-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 0;
  border-bottom: 1px solid rgba(0,0,0,0.05);
}

.su-toggle-row--last {
  border-bottom: none;
}

.su-toggle-label {
  font-size: 14px;
}

.su-toggle-hint {
  font-size: 12px;
  color: #999;
  margin-top: 1px;
}

.su-toggle {
  flex-shrink: 0;
}

/* ── Checkboxes ───────────────────────────────────────── */
.su-check {
  margin-top: 0 !important;
  padding-top: 0 !important;
}

/* ── Select ───────────────────────────────────────────── */
.su-select { border-radius: 8px; }

/* ── Color swatches ───────────────────────────────────── */
.su-color-swatches {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.su-color-dot {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.15s, box-shadow 0.15s;
  box-shadow: 0 2px 6px rgba(0,0,0,0.25);
}

.su-color-dot:hover {
  transform: scale(1.12);
  box-shadow: 0 4px 12px rgba(0,0,0,0.35);
}

.su-color-dot.active {
  outline: 2.5px solid rgba(255,255,255,0.85);
  outline-offset: 2px;
  transform: scale(1.08);
}

.theme--light .su-color-dot.active {
  outline-color: rgba(0,0,0,0.55);
}

/* ── Theme grid ───────────────────────────────────────── */
.su-theme-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}

.su-theme-option {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px 10px;
  border: 2px solid rgba(0,0,0,0.1);
  border-radius: 10px;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s;
  text-align: center;
}

.su-theme-option:hover {
  border-color: #22d3ee;
  background: rgba(34,211,238,0.04);
}

.su-theme-option.active {
  border-color: #22d3ee !important;
  background: rgba(34,211,238,0.08) !important;
}

.su-theme-label {
  margin-top: 6px;
  font-size: 13px;
  font-weight: 600;
}

.su-theme-desc {
  margin-top: 2px;
  font-size: 11px;
  color: #999;
}

.su-theme-option.active .su-theme-label { color: #22d3ee; }

/* ── Footer buttons ───────────────────────────────────── */
.su-btn-save {
  background-color: #22d3ee !important;
  color: #06222b !important;
  font-weight: 600;
  border-radius: 8px !important;
}

.su-btn-reset {
  border-color: rgba(0,0,0,0.2) !important;
  border-radius: 8px !important;
}
</style>

<style>
/* ── Dark mode overrides (Vuetify can't be scoped) ────── */
.theme--dark #settings-user { background: transparent; }

.theme--dark .su-card.v-card {
  background: #18181b !important;
  border-color: #262626 !important;
}

.theme--dark .su-card-title { color: rgba(255,255,255,0.92); }
.theme--dark .su-card-subtitle { color: #666; }

.theme--dark .su-divider { border-color: #262626 !important; }

.theme--dark .su-section-title { color: rgba(255,255,255,0.85); }
.theme--dark .su-section-label { color: #555; }
.theme--dark .su-field-label    { color: #777; }

.theme--dark .su-toggle-row { border-bottom-color: #1f1f1f; }
.theme--dark .su-toggle-label { color: rgba(255,255,255,0.82); }
.theme--dark .su-toggle-hint  { color: #555; }

/* Select / text-field inputs dark */
.theme--dark .su-select .v-input__slot,
.theme--dark .su-tab-body .v-text-field--outlined .v-input__slot {
  background-color: #0d0d0d !important;
}
.theme--dark .su-tab-body .v-text-field--outlined.v-input--is-focused fieldset {
  border-color: #22d3ee !important;
}
.theme--dark .su-tab-body .v-input--is-focused .v-label {
  color: #22d3ee !important;
}

/* Theme option cards dark */
.theme--dark .su-theme-option {
  border-color: #1f1f1f;
  background: #111;
}
.theme--dark .su-theme-option:hover {
  border-color: #22d3ee;
  background: rgba(34,211,238,0.05) !important;
}
.theme--dark .su-theme-option.active {
  border-color: #22d3ee !important;
  background: rgba(34,211,238,0.08) !important;
}
.theme--dark .su-theme-label { color: rgba(255,255,255,0.82); }
.theme--dark .su-theme-desc  { color: #555; }
.theme--dark .su-theme-option.active .su-theme-label { color: #22d3ee; }

/* Buttons dark */
.theme--dark .su-btn-reset {
  border-color: #333 !important;
  color: rgba(255,255,255,0.7) !important;
}

/* v-switch thumb track dark */
.theme--dark .su-toggle.v-input--switch .v-input__slot { background: transparent !important; }
</style>
